import React from 'react';
import { clsx } from 'clsx';
import { Loader2 } from 'lucide-react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'accent';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

export const Button: React.FC<ButtonProps> = ({ 
  children, 
  variant = 'primary', 
  size = 'md', 
  fullWidth = false, 
  isLoading = false,
  leftIcon,
  rightIcon,
  className = '', 
  disabled,
  ...props 
}) => {
  const baseStyles = "inline-flex items-center justify-center rounded-xl font-bold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed uppercase tracking-widest font-display";

  const variants = {
    primary: "bg-primary-dark text-white shadow-card hover:bg-primary hover:-translate-y-0.5 active:translate-y-0 active:shadow-sm focus:ring-primary border-2 border-primary-dark",
    secondary: "bg-surface text-primary-dark border-2 border-primary-dark/20 shadow-sm hover:bg-gray-50 active:scale-[0.98] focus:ring-gray-200",
    outline: "border-2 border-primary-dark text-primary-dark font-black hover:bg-primary/5 active:bg-primary/10 focus:ring-primary",
    ghost: "hover:bg-gray-100 text-primary-dark active:bg-gray-200 font-bold",
    danger: "bg-red-700 text-white shadow-sm hover:bg-red-600 active:translate-y-0 focus:ring-error border-2 border-red-900",
    accent: "bg-secondary text-primary-dark shadow-sm hover:bg-secondary-dark hover:text-white hover:-translate-y-0.5 active:translate-y-0 focus:ring-secondary-dark border-2 border-secondary-dark font-black"
  };

  const sizes = {
    sm: "h-9 px-4 text-[10px]",
    md: "h-12 px-6 text-xs",
    lg: "h-14 px-10 text-sm"
  };

  return (
    <button
      className={clsx(baseStyles, variants[variant], sizes[size], fullWidth && 'w-full', className)}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? (
        <Loader2 className="animate-spin" size={20} />
      ) : (
        <>
          {leftIcon && <span className="mr-2">{leftIcon}</span>}
          {children}
          {rightIcon && <span className="ml-2">{rightIcon}</span>}
        </>
      )}
    </button>
  );
};
